import { z } from "zod";
import { useState } from "react";
import Button from "components/Buttons";
import { motion } from "framer-motion";
import letusknowImage from "assets/images/LetUsKnow.png";
import { FormProvider, useForm } from "react-hook-form";
import ModalStore from "components/Modal/ModalStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "components/ui/input";
import { PARTNER_REQUEST_INFO_SECTION_ID } from "utils";

const LetUsKnow = () => {
  const schema = z.object({
    company: z.string().min(1, "Company name is required"),
    firstname: z.string().min(1, "First name is required"),
    lastname: z.string().min(1, "Last name is required"),
    phone: z.string()
      .min(1, "Phone number is required")
      .regex(
        /^\(?([2-9][0-8][0-9])\)?[-.●\s]?([2-9][0-9]{2})[-.●\s]?([0-9]{4})$/,
        "Invalid phone number format"
      ),
    email: z
      .string()
      .min(1, "Email is required")
      .email("Invalid email format"),
  });

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: {
      company: "",
      firstname: "",
      lastname: "",
      phone: "",
      email: "",
    },
  });

  const [loading, setLoading] = useState(false);

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, delay: 0.5 } },
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://wriil2sbhk.execute-api.us-east-1.amazonaws.com/dev/partner/subscribe",
        {
          method: "POST",
          headers: {
            "x-api-key": "ICNExLiUwH3c9AxGYXQXy73x34iiUWRC14qPJNYz",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            company: data.company,
            fname: data.firstname,
            lname: data.lastname,
            email: data.email,
            phone: data.phone.replace(/\D/g, ""), // Remove non-numeric characters
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        ModalStore.openSimpleSuccess({
          icon: undefined,
          title: "SUCCESS!",
          subtitle: result?.message,
          shouldCloseOnOverlayClick: false,
        });
        methods.reset();
      } else {
        const error = await response.text();
        console.error("API Error:", error);
        ModalStore.openSimpleError({
          title: "ERROR!",
          subtitle: "API submission failed. Please try again.",
          shouldCloseOnOverlayClick: false,
        });
      }
    } catch (error) {
      console.error("Error sending data to API:", error);
      ModalStore.openSimpleError({
        title: "ERROR!",
        subtitle: "Something went wrong. Please try again.",
        shouldCloseOnOverlayClick: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneNumberChange = (event) => {
    const input = event.target;
    const rawInput = input.value;
    const cursorPosition = input.selectionStart;
    const rawValue = rawInput.replace(/\D/g, "");
    const value = rawValue.slice(0, 10);
    let formattedValue = "";
    if (value.length > 6) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(
        3,
        6
      )}-${value.slice(6)}`;
    } else if (value.length > 3) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    } else if (value.length > 0) {
      formattedValue = `(${value}`;
    }

    let numericCursorPosition = 0;
    for (let i = 0; i < cursorPosition; i++) {
      if (/\d/.test(rawInput[i])) {
        numericCursorPosition++;
      }
    }

    let adjustedCursorPosition = 0;
    let numericCount = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        numericCount++;
      }
      if (numericCount >= numericCursorPosition) {
        adjustedCursorPosition = i + 1;
        break;
      }
    }

    setTimeout(() => {
      input.value = formattedValue;
      input.selectionStart = input.selectionEnd = Math.min(
        adjustedCursorPosition,
        formattedValue.length
      );
    }, 0);

    methods.setValue("phone", formattedValue);
  };

  return (
    <div
      id={PARTNER_REQUEST_INFO_SECTION_ID}
      className="scroll-mt-24 md:scroll-mt-28 bg-white mx-auto px-4 sm:px-6 pt-[37px] pb-8 sm:pb-10 md:pt-16 md:pb-16"
    >
      <div className="max-w-[1100px] mx-auto">
        <motion.h4
          className="uppercase font-barlowCondensed text-[#2B364B] text-center text-4xl md:text-5xl leading-tight font-bold mb-10 md:mb-14"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          transition={{ delay: 0.2 }}
        >
          Give your brand the SaddleFit Advantage
        </motion.h4>
      </div>

      <FormProvider {...methods}>
        <form
          className="w-full max-w-[1100px] mx-auto"
          onSubmit={methods.handleSubmit(onSubmit)}
          noValidate
        >
          <div className="flex flex-col md:flex-row md:items-stretch rounded-[32px] border border-[#E8EBF0] bg-white shadow-[0px_10px_15px_-3px_rgba(18,18,23,0.08),0px_4px_6px_-2px_rgba(18,18,23,0.05)] overflow-hidden">
            <div className="flex justify-center items-center px-8 pt-10 pb-6 md:py-10 md:pl-10 md:pr-6 lg:pl-12 lg:pr-8 md:w-[min(42%,340px)] lg:min-w-[300px] shrink-0 bg-gradient-to-b from-[#F4F7FB] to-[#EEF2F8] border-b border-[#E8EBF0]/80 md:border-b-0 md:border-r md:border-[#E8EBF0]/80">
              <img
                src={letusknowImage}
                alt="Western arena illustration with SaddleFit branding"
                className="w-full max-w-[280px] md:max-w-none md:w-full aspect-square object-contain select-none"
                width={640}
                height={640}
                decoding="async"
              />
            </div>

            <div className="flex flex-col flex-1 min-w-0 p-6 sm:p-8 md:py-10 md:pr-10 md:pl-8 lg:pr-12">
              <h4 className="text-lg sm:text-xl md:text-2xl md:leading-snug font-bold font-montserrat text-[#2B364B] mb-1">
                Request information to become a partner
              </h4>
              <p className="text-sm text-[#5c6a7e] font-montserrat mb-6 md:mb-8">
                We&apos;ll follow up with next steps for your dealership or brand.
              </p>

              <div className="w-full space-y-5">
                <div>
                  <Input
                    name="company"
                    label={"Company"}
                    placeholder={"Barrel Racers"}
                    autoComplete="organization"
                    {...methods?.register("company")}
                  />
                  {methods?.formState?.errors.company && (
                    <p className="text-red-500 text-sm font-montserrat mt-1.5">
                      {methods?.formState?.errors.company?.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-x-6">
                  <div className="min-w-0">
                    <Input
                      {...methods?.register("firstname")}
                      className="partner-input w-full"
                      name="firstname"
                      label={"First Name"}
                      placeholder={"Steve"}
                      autoComplete="given-name"
                    />
                    {methods?.formState?.errors.firstname && (
                      <p className="text-red-500 text-sm font-montserrat mt-1.5">
                        {methods?.formState?.errors.firstname?.message}
                      </p>
                    )}
                  </div>

                  <div className="min-w-0">
                    <Input
                      {...methods?.register("lastname")}
                      className="partner-input w-full"
                      name="lastname"
                      label={"Last Name"}
                      placeholder={"Smith"}
                      autoComplete="family-name"
                    />
                    {methods?.formState?.errors.lastname && (
                      <p className="text-red-500 text-sm font-montserrat mt-1.5">
                        {methods?.formState?.errors.lastname?.message}
                      </p>
                    )}
                  </div>

                  <div className="min-w-0">
                    <Input
                      {...methods?.register("phone")}
                      name="phone"
                      label={"Phone Number"}
                      type={"tel"}
                      placeholder={"(555) 555-5555"}
                      maxLength={14}
                      className="partner-input w-full"
                      onChange={handlePhoneNumberChange}
                      autoComplete="tel"
                    />
                    {methods?.formState?.errors.phone && (
                      <p className="text-red-500 text-sm font-montserrat mt-1.5">
                        {methods?.formState?.errors.phone?.message}
                      </p>
                    )}
                  </div>

                  <div className="min-w-0">
                    <Input
                      {...methods?.register("email")}
                      name="email"
                      label={"Email"}
                      placeholder={"example@sample.com"}
                      className="partner-input w-full"
                      autoComplete="email"
                    />
                    {methods?.formState?.errors.email && (
                      <p className="text-red-500 text-sm font-montserrat mt-1.5">
                        {methods?.formState?.errors.email?.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <Button
                primary
                disabled={loading}
                loading={loading}
                type="submit"
                whiteOutline
                className="!text-[15px] font-semibold font-montserrat mt-8 text-white rounded-[56px] w-full sm:w-auto sm:min-w-[160px] h-11 sm:h-12 px-8 self-stretch sm:self-start"
              >
                Submit
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default LetUsKnow;
